import React, { useEffect, useMemo, useState } from 'react';
import WidgetShell from './WidgetShell';
import { getStrategySheet } from './strategyDataCache';
import { resolveStrategyDisplayName } from './resolveStrategyDisplayName';
import { StrategyCumulativeChart } from '../StrategyDetailsView';
import {
  buildChartData,
  extractWeeklyData,
  makeCurrencyFormatters,
} from '../../utils/strategySheetDerivations';

function WeeklyChartWidget({ config, user, customCombos, editing, onRemove }) {
  const {
    strategy = '',
    contractType = 'NQ',
    contractSize = 1,
  } = config || {};

  const [rawRows, setRawRows] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    if (!strategy) {
      setRawRows(null);
      setError('Pick a strategy in the widget settings.');
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    getStrategySheet({ strategy, email: user?.email })
      .then((data) => {
        if (cancelled) return;
        setRawRows(Array.isArray(data?.rows) ? data.rows : []);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.message || 'Failed to load strategy data');
        setLoading(false);
      });
    return () => { cancelled = true; };
  }, [strategy, user?.email, reloadToken]);

  const weeklyData = useMemo(() => extractWeeklyData(rawRows || []), [rawRows]);

  const chartData = useMemo(
    () => buildChartData({
      weeklyData,
      contractType,
      contractSize: Number(contractSize) || 1,
      sheetName: strategy,
    }),
    [weeklyData, contractType, contractSize, strategy],
  );

  const { formatCurrencyFull, formatAxisCurrency } = useMemo(
    () => makeCurrencyFormatters({ contractType, sheetName: strategy, contractSize: Number(contractSize) || 1 }),
    [contractType, strategy, contractSize],
  );

  const displayStrategyName = resolveStrategyDisplayName(strategy, customCombos);
  return (
    <WidgetShell
      title={`Cumulative P&L · ${displayStrategyName || '—'}`}
      subtitle={`${contractType} · ${Number(contractSize) || 1}x default contract size`}
      editing={editing}
      onRemove={onRemove}
      loading={loading}
      error={error}
      onRetry={() => setReloadToken((t) => t + 1)}
      bodyClassName="dashboard-widget-body-chart"
    >
      {chartData.length === 0 ? (
        <div className="dashboard-widget-state">No daily data to chart yet.</div>
      ) : (
        <StrategyCumulativeChart
          chartData={chartData}
          formatCurrencyFull={formatCurrencyFull}
          formatAxisCurrency={formatAxisCurrency}
          contractType={contractType}
          showRollWeek={false}
          showHighImpactUsd={false}
          highImpactEventsByDate={null}
        />
      )}
    </WidgetShell>
  );
}

export default WeeklyChartWidget;
